import { Avatar, Button, Collapse, Grid, IconButton, Paper, Typography, colors } from '@material-ui/core';
import { unstable_Box as Box } from '@material-ui/core/Box';
import { withStyles } from '@material-ui/core/styles';
import withWidth, { isWidthUp } from '@material-ui/core/withWidth';
import {
  Face as FaceIcon,
  Lock as LockIcon,
  People as PeopleIcon,
  FiberManualRecord as PersonIcon,
} from '@material-ui/icons';
import React, { useEffect, useState } from 'react';

import { Booking } from '@aps-management/primapp-common';

import i18n from '_utils/i18n';

import DeficiencyBlockAction from './DeficiencyBlockAction';

/* */
const styles = (theme) => ({
  timeslot: {
    padding: theme.spacing.unit * 1.5,
    paddingTop: theme.spacing.unit * 1,
  },
  time: {
    minWidth: 66,
    fontWeight: 'bold',
  },
  players: {
    fontSize: 10,
    color: theme.palette.grey[300],
  },
  player: {
    color: colors.green[500],
  },
  people: {
    marginRight: theme.spacing.unit * 1,
  },
  others: {
    marginTop: theme.spacing.unit * 1.5,
  },
  other: {
    padding: theme.spacing.unit * 1.5,
  },
  price: {
    color: theme.palette.text.secondary,
  },
  initPrice: {
    color: theme.palette.grey[500],
    textDecoration: 'line-through',
    marginRight: theme.spacing.unit * 1.5,
  },
  hcp: {
    fontSize: 13,
    fontWeight: 'bold',
    marginRight: theme.spacing.unit * 1,
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.dark,
  },
});

const OtherPlayer = withWidth()((props) => {
  const { classes, width, data, showLocations } = props;

  const isDesktop = isWidthUp('md', width);
  const br = isDesktop ? <br /> : '\xa0'; // Espace insécable
  const isAnonymous = !data.firstname && !data.lastname;

  const name = isAnonymous ? (
    <>Joueur{br}Anonyme</>
  ) : (
    <>
      {data.firstname}
      {data.firstname && br}
      {data.lastname}
    </>
  );

  const location =
    showLocations && data.location ? (
      <span title={data.location.name}>{` (${data.location.name[0]})`.toUpperCase()}</span>
    ) : null;

  return (
    <Grid item sm={6} xs={12} className={classes.other}>
      <Box display='flex' alignItems='flex-start' flexDirection='row'>
        <Avatar className={classes.hcp}>{data.index ? data.index.toFixed(1) : <FaceIcon />}</Avatar>
        <Typography variant='body2'>
          {name}
          {location}
        </Typography>
      </Box>
    </Grid>
  );
});

const Timeslot = (props) => {
  const [showOtherPlayers, setShowOtherPlayers] = useState(false);

  const { data, onBook, classes, showPlanning, displayFlags } = props;

  const { free, others, deficiency, availability, isLockedByMe, isTemporarilyLocked } = data;
  const { price, discount, initPrice } = data.total;

  useEffect(() => {
    setShowOtherPlayers(false);
  }, [showPlanning]);

  if (!showPlanning && !free) return null;

  const { showAvailability, showOtherPlayerLocations } = displayFlags;

  const hasOthers = Boolean(others && others.length);
  const showOthersToggleButton = displayFlags.showOthers && !showPlanning;
  const showOthers = showPlanning || showOtherPlayers;

  const showPricing = price > 0;
  const hasMissingItems = deficiency.total > 0;

  const renderAvailabilityIcons = () => (
    <div className={classes.players} title={i18n.t('terms.availability', { count: availability })}>
      {Array.from({ length: availability }, (_, i) => (
        <PersonIcon key={`free-${i}`} className={classes.player} fontSize='inherit' />
      ))}
      {others?.map((_o, i) => (
        <PersonIcon key={`other-${i}`} fontSize='inherit' />
      ))}
    </div>
  );

  const bookButtonProps = {
    onClick: onBook,
    disabled: false,
  };
  let bookButtonContent = 'Réserver';

  if (isTemporarilyLocked && !isLockedByMe) {
    bookButtonProps.disabled = true;
    bookButtonContent = <LockIcon />;
  } else if (!free) {
    bookButtonProps.disabled = true;
    bookButtonContent = availability > 0 ? 'Indisponible' : 'Complet';
  }

  const bookButton = (
    <Button fullWidth size='large' color='secondary' variant='contained' {...bookButtonProps}>
      {bookButtonContent}
    </Button>
  );

  const renderBookingAction = () => {
    if (!hasMissingItems) {
      return bookButton;
    }

    return <DeficiencyBlockAction deficiency={deficiency} bookButton={bookButton} />;
  };

  return (
    <Grid item md={6} sm={10} xs={12}>
      <Paper className={classes.timeslot}>
        <Box display='flex' alignItems='center' flexDirection='row'>
          <Typography className={classes.time} component='span' variant='h6'>
            {Booking.functions.formatTime(data.time)}
            {showAvailability && renderAvailabilityIcons()}
          </Typography>

          {showOthersToggleButton && (
            <IconButton
              size='small'
              disabled={!hasOthers}
              className={classes.people}
              onClick={() => setShowOtherPlayers((prev) => !prev)}
            >
              <PeopleIcon />
            </IconButton>
          )}

          {showPricing && (
            <Box flex={1} display='flex' alignItems='center' flexDirection='row' justifyContent='flex-end'>
              {discount > 0 && (
                <Typography className={classes.initPrice} variant='body1' component='span'>
                  {i18n.l('currency', initPrice / 100)}
                </Typography>
              )}
              <Typography className={classes.price} variant='h6' component='span'>
                {i18n.l('currency', price / 100)}
              </Typography>
            </Box>
          )}

          {!showPricing && !hasMissingItems && renderBookingAction()}
        </Box>

        {(showPricing || hasMissingItems) && <Box mt={1}>{renderBookingAction()}</Box>}

        {hasOthers && (
          <Collapse timeout={100} in={showOthers}>
            <Grid container spacing={8} className={classes.others}>
              {others?.map((o, i) => (
                <OtherPlayer key={i} data={o} classes={classes} showLocations={showOtherPlayerLocations} />
              ))}
            </Grid>
          </Collapse>
        )}
      </Paper>
    </Grid>
  );
};

export default withStyles(styles)(Timeslot);
